import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import userRoleService from '../../services/userRoleService';

interface Props {
  profession: string;
  activatedRolesCount: number;
  onRemoved: () => void;
}

const TutorRoleSection: React.FC<Props> = ({ profession: initialProfession, activatedRolesCount, onRemoved }) => {
  const navigate = useNavigate();
  const [profession, setProfession] = useState(initialProfession);
  const [professionSaved, setProfessionSaved] = useState(false);
  const [savingProfession, setSavingProfession] = useState(false);
  const [professionError, setProfessionError] = useState<string | null>(null);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const isLastRole = activatedRolesCount <= 1;

  const handleSaveProfession = async () => {
    if (!profession.trim()) return;
    setSavingProfession(true);
    setProfessionError(null);
    setProfessionSaved(false);
    try {
      await userRoleService.updateRoleProfile('TUTOR', { profession });
      setProfessionSaved(true);
      setTimeout(() => setProfessionSaved(false), 3000);
    } catch (err: any) {
      setProfessionError(err.message || 'Failed to save profession');
    } finally {
      setSavingProfession(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    setRemoveError(null);
    try {
      await userRoleService.removeRole('TUTOR');
      onRemoved();
      if (isLastRole) {
        navigate('/role-selection', { replace: true });
      } else {
        setRemoveDialogOpen(false);
      }
    } catch (err: any) {
      setRemoveError(err.message || 'Failed to remove Tutor role');
      setRemoving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Tutor Role</Typography>

      {professionError && <Alert severity="error" sx={{ mb: 2 }}>{professionError}</Alert>}
      {professionSaved && <Alert severity="success" sx={{ mb: 2 }}>Profession saved.</Alert>}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 3 }}>
        <TextField
          label="Current Profession"
          value={profession}
          onChange={e => setProfession(e.target.value)}
          size="small"
          sx={{ flex: 1 }}
        />
        <Button
          variant="outlined"
          onClick={handleSaveProfession}
          disabled={savingProfession || !profession.trim() || profession === initialProfession}
          endIcon={savingProfession ? <CircularProgress size={16} /> : null}
          sx={{ mt: 0.5 }}
        >
          Save
        </Button>
      </Box>

      <Button
        variant="outlined"
        color="error"
        onClick={() => setRemoveDialogOpen(true)}
      >
        Remove Tutor Role
      </Button>

      <Dialog open={removeDialogOpen} onClose={() => !removing && setRemoveDialogOpen(false)} maxWidth="xs">
        <DialogTitle>Remove Tutor Role?</DialogTitle>
        <DialogContent>
          {removeError && <Alert severity="error" sx={{ mb: 2 }}>{removeError}</Alert>}
          <DialogContentText>
            Your Tutor profile and education data will be preserved. All your courses will be
            soft-archived and can be restored when you re-activate the Tutor role.
          </DialogContentText>
          {isLastRole && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This is your only active role. Removing it means you will have no active role —
              you will need to activate one at your next sign-in.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)} disabled={removing}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleRemove} disabled={removing}
            endIcon={removing ? <CircularProgress size={16} /> : null}>
            {removing ? 'Removing…' : 'Remove Role'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TutorRoleSection;
